/**
 * Painel do Will.
 *
 * Duas funções, nesta ordem de importância:
 *
 * 1. Trocar o modo de voz (ADR-007). A decisão é do usuário, em tempo de uso,
 *    não de configuração — por isso o seletor está aqui e não num .env.
 * 2. Dizer a verdade sobre o que está ligado. Se o Realtime não tem chave, o
 *    painel escreve isso com todas as letras em vez de mostrar um botão que
 *    não faz nada. Um painel que mente é pior que painel nenhum: some com o
 *    sintoma e mantém o defeito.
 *
 * As cores saem todas de DOME_PALETTE ('@will/contracts'). O painel divide a
 * tela com o domo; se tivesse paleta própria seriam duas identidades brigando.
 */

import { useMemo, type CSSProperties, type ReactElement } from 'react';
import {
  DOME_PALETTE,
  VOICE_MODES,
  type ConversationState,
  type VoiceMode,
} from '@will/contracts';
import { DEFAULT_API_BASE, type DependencyHealth, type HealthReading } from './health';
import { useHealth } from './useHealth';
import { VozBancada } from './VozBancada';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PanelProps {
  /** modo de voz em vigor — o painel é controlado, quem guarda o estado é o app */
  mode: VoiceMode;
  onModeChange: (mode: VoiceMode) => void;
  /** estado do stream, quando o app já tem um; ausente = o painel não opina */
  conversationState?: ConversationState;
  /** ADR-008: mesma origem. Sobrescrever só faz sentido em teste. */
  apiBase?: string;
  /** desabilita a troca enquanto o app reconfigura a sessão Realtime */
  busy?: boolean;
  className?: string;
}

// ---------------------------------------------------------------------------
// Descrição dos modos — texto vindo da tabela do ADR-007
// ---------------------------------------------------------------------------

interface ModeInfo {
  label: string;
  /** uma linha, no infinitivo do que acontece */
  summary: string;
  latency: string;
  /** dependências que precisam estar configuradas para o modo funcionar */
  requires: readonly ('realtime' | 'omnivoice')[];
}

const MODE_INFO: Record<VoiceMode, ModeInfo> = {
  hybrid: {
    label: 'Híbrido',
    summary: 'O Realtime escuta, o OmniVoice fala com a voz do Diego.',
    latency: '~2 s até o primeiro áudio',
    requires: ['realtime', 'omnivoice'],
  },
  realtime: {
    label: 'Realtime',
    summary: 'O Realtime escuta e fala. A voz é da OpenAI, não a do Diego.',
    latency: 'abaixo de 1 s',
    requires: ['realtime'],
  },
  text: {
    label: 'Texto',
    summary: 'O Realtime escuta e transcreve. Nenhum áudio de saída.',
    latency: 'sem áudio',
    requires: ['realtime'],
  },
};

const DEPENDENCY_LABEL = {
  realtime: 'Realtime (escuta)',
  omnivoice: 'OmniVoice (voz do Diego)',
  brain: 'Will Brain (trabalho profundo)',
} as const;

type DependencyKey = keyof typeof DEPENDENCY_LABEL;

// ---------------------------------------------------------------------------
// Leitura honesta do health
// ---------------------------------------------------------------------------

type Availability = 'on' | 'off' | 'unknown';

function availabilityOf(dep: DependencyHealth | undefined): Availability {
  if (!dep || dep.configured === null || dep.configured === undefined) return 'unknown';
  return dep.configured ? 'on' : 'off';
}

const AVAILABILITY_TEXT: Record<Availability, { chip: string; color: string }> = {
  on: { chip: 'ligado', color: DOME_PALETTE.growth },
  off: { chip: 'sem credencial', color: DOME_PALETTE.alert },
  unknown: { chip: 'não informado', color: DOME_PALETTE.pending },
};

/** Estado conversacional → cor e rótulo do indicador de conexão. */
const CONVERSATION_LABEL: Record<ConversationState, { label: string; color: string }> = {
  IDLE:               { label: 'ocioso',            color: DOME_PALETTE.muted },
  CONNECTING:         { label: 'conectando',        color: DOME_PALETTE.effort },
  LISTENING:          { label: 'ouvindo',           color: DOME_PALETTE.signal },
  USER_SPEAKING:      { label: 'você falando',      color: DOME_PALETTE.signal },
  THINKING:           { label: 'pensando',          color: DOME_PALETTE.signal },
  PREAMBLE:           { label: 'preâmbulo',         color: DOME_PALETTE.signal },
  TOOL_RUNNING:       { label: 'executando',        color: DOME_PALETTE.effort },
  WAITING_APPROVAL:   { label: 'aguardando você',   color: DOME_PALETTE.pending },
  ASSISTANT_SPEAKING: { label: 'falando',           color: DOME_PALETTE.signal },
  INTERRUPTED:        { label: 'interrompido',      color: DOME_PALETTE.pending },
  RECONNECTING:       { label: 'reconectando',      color: DOME_PALETTE.effort },
  PRIVATE:            { label: 'modo privado',      color: DOME_PALETTE.deep },
  ERROR:              { label: 'erro',              color: DOME_PALETTE.alert },
};

/**
 * O aviso que aparece em destaque. Devolve `null` quando não há nada honesto a
 * alertar — banner permanente vira decoração e para de ser lido.
 */
function warningFor(mode: VoiceMode, reading: HealthReading): string | null {
  if (reading.phase === 'loading') return null;

  if (reading.phase === 'unreachable' || !reading.health) {
    const why = reading.error ? ` (${reading.error})` : '';
    return `Não consegui falar com a API em ${DEFAULT_API_BASE}/health${why}. `
      + 'Não sei o que está ligado do outro lado — o que está abaixo é a última leitura, ou nada.';
  }

  const { realtime, omnivoice } = reading.health;
  const need = MODE_INFO[mode].requires;

  if (need.includes('realtime') && availabilityOf(realtime) === 'off') {
    return 'O Realtime está sem chave. Sem ele não há escuta por voz em nenhum dos '
      + 'três modos: dá para digitar, não para falar.';
  }

  if (need.includes('omnivoice') && availabilityOf(omnivoice) === 'off') {
    return 'O OmniVoice está sem endereço ou sem token. No modo híbrido é ele quem fala — '
      + 'sem ele o Will escuta e responde por escrito, mas não abre a boca.';
  }

  const server = reading.health.serverVoiceMode;
  if (server !== null && server !== mode) {
    return `O servidor está configurado em "${MODE_INFO[server].label}" e o painel está em `
      + `"${MODE_INFO[mode].label}". A troca só vale a partir do próximo turno (ADR-007).`;
  }

  return null;
}

/** Um modo só é bloqueado quando o health afirmou que falta a dependência. */
function unavailableReason(mode: VoiceMode, reading: HealthReading): string | null {
  if (reading.phase !== 'ok' || !reading.health) return null;

  const health = reading.health;
  for (const dep of MODE_INFO[mode].requires) {
    if (availabilityOf(health[dep]) === 'off') {
      return `indisponível: ${DEPENDENCY_LABEL[dep]} sem credencial`;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export function Panel({
  mode,
  onModeChange,
  conversationState,
  apiBase = DEFAULT_API_BASE,
  busy = false,
  className,
}: PanelProps): ReactElement {
  const reading = useHealth(apiBase);
  const warning = useMemo(() => warningFor(mode, reading), [mode, reading]);

  const link = connectionOf(reading, conversationState);

  const dependencies: DependencyKey[] = ['realtime', 'omnivoice', 'brain'];

  return (
    <section
      className={className ? `u1-panel ${className}` : 'u1-panel'}
      aria-label="Painel do Will"
      style={PALETTE_VARS}
    >
      <style>{PANEL_CSS}</style>

      <header className="u1-panel__head">
        <h2 className="u1-panel__title">Painel</h2>
        <span className="u1-chip" style={{ '--chip': link.color } as CSSProperties}>
          <span className="u1-dot" aria-hidden="true" />
          {link.label}
        </span>
      </header>

      {/* --- modo de voz (ADR-007) --------------------------------------- */}
      <fieldset className="u1-panel__group" disabled={busy}>
        <legend className="u1-panel__legend">Modo de voz</legend>

        <div className="u1-modes" role="radiogroup" aria-label="Modo de voz">
          {VOICE_MODES.map((m) => {
            const info = MODE_INFO[m];
            const blocked = unavailableReason(m, reading);
            const active = m === mode;

            return (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={active}
                className={`u1-mode${active ? ' u1-mode--on' : ''}${blocked ? ' u1-mode--blocked' : ''}`}
                // um modo sem dependência continua clicável: bloquear a escolha
                // esconderia o motivo. Ele é marcado e explicado, não sumido.
                onClick={() => onModeChange(m)}
              >
                <span className="u1-mode__label">{info.label}</span>
                <span className="u1-mode__summary">{info.summary}</span>
                <span className="u1-mode__meta">
                  {info.latency}
                  {blocked ? <> · <span className="u1-mode__blocked">{blocked}</span></> : null}
                </span>
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* --- bancada de vozes ------------------------------------------- */}
      <VozBancada />


      {/* --- aviso honesto ------------------------------------------------ */}
      {warning ? (
        <p className="u1-warning" role="status">{warning}</p>
      ) : null}

      {/* --- o que está ligado -------------------------------------------- */}
      <div className="u1-panel__group">
        <h3 className="u1-panel__legend">O que está ligado</h3>

        <ul className="u1-deps">
          {dependencies.map((dep) => {
            const state = availabilityOf(reading.health?.[dep]);
            const text = AVAILABILITY_TEXT[state];
            return (
              <li key={dep} className="u1-dep">
                <span className="u1-dep__name">{DEPENDENCY_LABEL[dep]}</span>
                <span className="u1-dep__detail">{reading.health?.[dep].detail ?? ''}</span>
                <span className="u1-chip" style={{ '--chip': text.color } as CSSProperties}>
                  <span className="u1-dot" aria-hidden="true" />
                  {reading.phase === 'loading' ? 'consultando…' : text.chip}
                </span>
              </li>
            );
          })}
        </ul>

        <p className="u1-foot">
          {describeReading(reading)}
          {' '}
          <button type="button" className="u1-link" onClick={reading.refresh}>
            consultar de novo
          </button>
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Auxiliares de apresentação
// ---------------------------------------------------------------------------

function connectionOf(
  reading: HealthReading,
  conversationState: ConversationState | undefined,
): { label: string; color: string } {
  // o estado do stream, quando existe, é a informação mais fresca sobre a
  // conexão; o health só diz se a API responde
  if (conversationState !== undefined) {
    const c = CONVERSATION_LABEL[conversationState];
    return { label: `stream: ${c.label}`, color: c.color };
  }

  if (reading.phase === 'loading') return { label: 'consultando…', color: DOME_PALETTE.effort };
  if (reading.phase === 'unreachable') return { label: 'API fora de alcance', color: DOME_PALETTE.alert };

  switch (reading.health?.status) {
    case 'ok':       return { label: 'API respondendo', color: DOME_PALETTE.growth };
    case 'degraded': return { label: 'API degradada', color: DOME_PALETTE.pending };
    case 'down':     return { label: 'API se declarou fora', color: DOME_PALETTE.alert };
    default:         return { label: 'API respondeu sem status', color: DOME_PALETTE.pending };
  }
}

function describeReading(reading: HealthReading): string {
  if (reading.checkedAt === null) return 'Primeira consulta em andamento.';

  const hora = new Date(reading.checkedAt).toLocaleTimeString('pt-BR');
  const versao = reading.health?.version ? ` · versão ${reading.health.version}` : '';

  return reading.phase === 'ok'
    ? `Lido às ${hora}${versao}.`
    : `Última tentativa às ${hora}, sem resposta.`;
}

// ---------------------------------------------------------------------------
// Estilo
// ---------------------------------------------------------------------------

/**
 * A paleta entra como variáveis CSS a partir de DOME_PALETTE — assim o contrato
 * continua sendo a única fonte das cores, e o CSS abaixo não repete valor
 * nenhum. O fundo é quase preto de propósito: o domo brilha, o painel não
 * disputa com ele.
 */
const PALETTE_VARS = {
  '--u1-signal': DOME_PALETTE.signal,
  '--u1-growth': DOME_PALETTE.growth,
  '--u1-deep': DOME_PALETTE.deep,
  '--u1-alert': DOME_PALETTE.alert,
  '--u1-pending': DOME_PALETTE.pending,
  '--u1-muted': DOME_PALETTE.muted,
  '--u1-calm': DOME_PALETTE.calm,
} as CSSProperties;

const PANEL_CSS = `
.u1-panel {
  --u1-bg: #0a0d12;
  --u1-surface: #11161d;
  --u1-line: #1e2731;
  --u1-text: #dfe7ef;
  --u1-dim: #8496a8;

  display: flex;
  flex-direction: column;
  gap: 20px;
  box-sizing: border-box;
  width: 100%;
  max-width: 420px;
  padding: 20px;
  border: 1px solid var(--u1-line);
  border-radius: 14px;
  background: var(--u1-bg);
  color: var(--u1-text);
  font: 14px/1.5 system-ui, -apple-system, "Segoe UI", sans-serif;
}
.u1-panel *, .u1-panel *::before, .u1-panel *::after { box-sizing: inherit; }

.u1-panel__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.u1-panel__title { margin: 0; font-size: 15px; font-weight: 600; letter-spacing: .02em; }

.u1-panel__group { min-width: 0; margin: 0; padding: 0; border: 0; }
.u1-panel__legend {
  margin: 0 0 10px; padding: 0;
  font-size: 11px; font-weight: 600; letter-spacing: .09em; text-transform: uppercase;
  color: var(--u1-dim);
}
.u1-panel__group[disabled] { opacity: .55; }

/* chip de estado — a cor vem sempre de --chip, definido em linha pela paleta */
.u1-chip {
  display: inline-flex; align-items: center; gap: 6px; flex: none;
  padding: 3px 9px; border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--chip) 35%, transparent);
  background: color-mix(in srgb, var(--chip) 12%, transparent);
  color: var(--chip);
  font-size: 11px; white-space: nowrap;
}
.u1-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--chip);
  box-shadow: 0 0 8px var(--chip);
}

.u1-modes { display: flex; flex-direction: column; gap: 8px; }
.u1-mode {
  display: flex; flex-direction: column; gap: 3px;
  padding: 11px 13px; border-radius: 10px; text-align: left;
  border: 1px solid var(--u1-line);
  background: var(--u1-surface);
  color: inherit; font: inherit; cursor: pointer;
  transition: border-color .15s, background .15s;
}
.u1-mode:hover { border-color: color-mix(in srgb, var(--u1-signal) 45%, var(--u1-line)); }
.u1-mode:focus-visible { outline: 2px solid var(--u1-signal); outline-offset: 2px; }
.u1-mode--on {
  border-color: var(--u1-signal);
  background: color-mix(in srgb, var(--u1-signal) 10%, var(--u1-surface));
}
.u1-mode--blocked { border-style: dashed; }
.u1-mode__label { font-weight: 600; }
.u1-mode--on .u1-mode__label { color: var(--u1-signal); }
.u1-mode__summary { color: var(--u1-dim); font-size: 12.5px; }
.u1-mode__meta { color: var(--u1-dim); font-size: 11.5px; }
.u1-mode__blocked { color: var(--u1-alert); }

.u1-warning {
  margin: 0; padding: 11px 13px; border-radius: 10px;
  border: 1px solid color-mix(in srgb, var(--u1-pending) 40%, transparent);
  background: color-mix(in srgb, var(--u1-pending) 10%, transparent);
  color: color-mix(in srgb, var(--u1-pending) 85%, #ffffff);
  font-size: 12.5px;
}

.u1-deps { display: flex; flex-direction: column; gap: 8px; margin: 0; padding: 0; list-style: none; }
.u1-dep {
  display: grid; align-items: center; gap: 2px 10px;
  grid-template-columns: 1fr auto;
  padding: 9px 12px; border-radius: 10px;
  border: 1px solid var(--u1-line); background: var(--u1-surface);
}
.u1-dep__name { min-width: 0; }
.u1-dep__detail {
  grid-column: 1; color: var(--u1-dim); font-size: 11.5px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.u1-dep__detail:empty { display: none; }
.u1-dep .u1-chip { grid-column: 2; grid-row: 1 / span 2; }

.u1-foot { margin: 12px 0 0; color: var(--u1-dim); font-size: 11.5px; }
/* ATENCAO: esta folha e INJETADA por este componente, e por isso vence o
   App.css sem aparecer em nenhuma busca la. Ja custou caro uma vez — o
   max-width de 420px que segurava o painel em tres colunas morava aqui — e
   custou de novo em 21/08, quando o alvo de toque do "sair" foi corrigido no
   App.css e continuou medindo 22px porque este padding zero ganhava.

   Se for mexer em .u1-link, mexa NOS DOIS lugares, ou traga a regra para o
   App.css e apague daqui.

   E nao use crase aqui dentro: isto vive num template literal, e a crase
   fecha a string. Custou um build quebrado. */
/* .u1-link nao mora mais aqui — ver o comentario acima. A regra unica esta no
   App.css, com o alvo de toque de 32px. */
.u1-link:focus-visible { outline: 2px solid var(--u1-signal); outline-offset: 2px; }

@media (prefers-reduced-motion: reduce) {
  .u1-mode { transition: none; }
}
`;

export default Panel;
